import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { PageWithTitle } from 'src/Common/components';
import {
  usePositionsGrouped,
  usePositionStats,
  useSyncPositions,
  usePredictionPerformance,
  useCreateCashflow
} from '../api/usePositions';
import { PredictionPosition, PositionsGroupedByPair } from '../types';

const formatUsd = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return '$' + value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatPrice = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return value.toFixed(4);
};

const formatContracts = (value: number | undefined) => {
  if (value == null) return '-';
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const pnlColor = (pnl: number | undefined) => {
  if (pnl == null) return 'inherit';
  if (pnl > 0) return '#4caf50';
  if (pnl < 0) return '#f44336';
  return 'inherit';
};

type SortField = 'pnl' | 'net' | 'makerTna' | 'takerTna' | 'daysToExpiry';
type SortDir = 'asc' | 'desc';

/** Small label + value stacked */
const Val = ({
  label,
  value,
  color,
  bold
}: {
  label: string;
  value: string;
  color?: string;
  bold?: boolean;
}) => (
  <Box sx={{ minWidth: 48, textAlign: 'center' }}>
    <Typography
      sx={{
        fontSize: '0.5rem',
        color: 'grey.600',
        lineHeight: 1,
        mb: 0.2,
        textTransform: 'uppercase',
        letterSpacing: '0.03em'
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.75rem',
        color: color || 'grey.300',
        fontWeight: bold ? 600 : 400,
        whiteSpace: 'nowrap',
        lineHeight: 1.3
      }}
    >
      {value}
    </Typography>
  </Box>
);

/** One platform's position: title + side chip + values */
const PositionCell = ({
  position,
  title
}: {
  position: PredictionPosition;
  title?: string;
}) => {
  const invested = position.avgPrice * position.contracts;
  // P&L based on top-of-book bid (what you'd get if you sell now)
  const bidPnl =
    position.topBid != null
      ? (position.topBid - position.avgPrice) * position.contracts
      : null;
  return (
    <Box sx={{ minWidth: 0, py: 0.3 }}>
      {title && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.3}
          mb={0.3}
          sx={{ minWidth: 0 }}
        >
          <Typography
            sx={{
              fontSize: '0.72rem',
              color: 'grey.400',
              lineHeight: 1.2,
              minWidth: 0
            }}
            noWrap
            title={title}
          >
            {title}
          </Typography>
          {position.marketUrl && (
            <IconButton
              size="small"
              component="a"
              href={position.marketUrl}
              target="_blank"
              rel="noopener"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ p: 0.2, flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: '0.65rem', color: 'grey.600' }} />
            </IconButton>
          )}
        </Stack>
      )}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Chip
          label={position.side.toUpperCase()}
          size="small"
          color={position.side === 'yes' ? 'success' : 'error'}
          sx={{
            fontSize: '0.6rem',
            fontWeight: 700,
            height: 18,
            minWidth: 28,
            borderRadius: '3px'
          }}
        />
        <Val label="avg" value={formatPrice(position.avgPrice)} />
        <Val label="bid" value={formatPrice(position.topBid)} color="#ff9800" />
        <Val label="ask" value={formatPrice(position.topAsk)} color="#ff9800" />
        <Val
          label="qty"
          value={formatContracts(position.contracts)}
          color="#64b5f6"
          bold
        />
        <Val label="invested" value={formatUsd(invested)} />
        <Val
          label="P&L"
          value={formatUsd(bidPnl != null ? bidPnl : position.unrealizedPnl)}
          color={pnlColor(bidPnl != null ? bidPnl : position.unrealizedPnl)}
          bold
        />
      </Stack>
    </Box>
  );
};

/** Ended position cell: no bid/ask, shows avg buy, qty, invested, P&L */
const EndedPositionCell = ({
  position,
  title
}: {
  position: PredictionPosition;
  title?: string;
}) => {
  const invested = position.avgPrice * position.contracts;
  return (
    <Box sx={{ minWidth: 0, py: 0.3 }}>
      {title && (
        <Stack direction="row" alignItems="center" spacing={0.3} mb={0.3} sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: '0.72rem', color: 'grey.400', lineHeight: 1.2, minWidth: 0 }}
            noWrap
            title={title}
          >
            {title}
          </Typography>
          {position.marketUrl && (
            <IconButton
              size="small"
              component="a"
              href={position.marketUrl}
              target="_blank"
              rel="noopener"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ p: 0.2, flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: '0.65rem', color: 'grey.600' }} />
            </IconButton>
          )}
        </Stack>
      )}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Chip
          label={position.side.toUpperCase()}
          size="small"
          color={position.side === 'yes' ? 'success' : 'error'}
          sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, minWidth: 28, borderRadius: '3px' }}
        />
        <Val label="avg buy" value={formatPrice(position.avgPrice)} />
        <Val label="qty" value={formatContracts(position.contracts)} color="#64b5f6" bold />
        <Val label="invested" value={formatUsd(invested)} />
        <Val
          label="P&L"
          value={formatUsd(position.unrealizedPnl)}
          color={pnlColor(position.unrealizedPnl)}
          bold
        />
      </Stack>
    </Box>
  );
};

/** Summary for ended rows: invested + P&L only */
const EndedSummaryCell = ({ group }: { group: PositionsGroupedByPair }) => {
  const totalPnl = group.totalUnrealizedPnl ?? 0;
  return (
    <Stack direction="row" alignItems="center" spacing={0.4} sx={{ py: 0.3 }}>
      <Box sx={{ width: COL.invested, textAlign: 'right' }}>
        <Typography sx={{ fontSize: '0.78rem', color: 'grey.400', whiteSpace: 'nowrap' }}>
          {formatUsd(group.totalInvested)}
        </Typography>
      </Box>
      <Box sx={{ width: COL.pnl, textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 600,
            color: pnlColor(totalPnl),
            whiteSpace: 'nowrap'
          }}
        >
          {formatUsd(totalPnl)}
        </Typography>
      </Box>
    </Stack>
  );
};

const EndedPairRow = ({ group }: { group: PositionsGroupedByPair }) => {
  const polyPos = group.positions.find(p => p.platform === 'polymarket');
  const kalshiPos = group.positions.find(p => p.platform === 'kalshi');
  const isUnmatched = !polyPos || !kalshiPos;

  const polyTitle =
    group.polymarketTitle || polyPos?.pairName || group.pairName || 'Unknown';
  const kalshiTitle =
    group.kalshiTitle || kalshiPos?.pairName || group.pairName || 'Unknown';

  return (
    <Stack
      direction="row"
      alignItems="stretch"
      sx={{
        px: 2,
        py: 0.5,
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        ...(isUnmatched && { backgroundColor: 'rgba(255, 152, 0, 0.06)' }),
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' }
      }}
    >
      <Box sx={{ flex: '0 1 40%', minWidth: 0 }}>
        {polyPos ? <EndedPositionCell position={polyPos} title={polyTitle} /> : <EmptyCell />}
      </Box>
      <Box sx={{ width: '1px', mx: 1, backgroundColor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{ flex: '0 1 40%', minWidth: 0 }}>
        {kalshiPos ? <EndedPositionCell position={kalshiPos} title={kalshiTitle} /> : <EmptyCell />}
      </Box>
      <Box sx={{ width: '1px', mx: 1, backgroundColor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{ flexShrink: 0, ml: 'auto', display: 'flex', alignItems: 'center' }}>
        {isUnmatched && (
          <Chip
            label="UNMATCHED"
            size="small"
            sx={{
              mr: 0.8,
              height: 18,
              fontSize: '0.58rem',
              fontWeight: 700,
              color: '#ffb74d',
              borderColor: 'rgba(255, 183, 77, 0.45)'
            }}
            variant="outlined"
          />
        )}
        <EndedSummaryCell group={group} />
      </Box>
    </Stack>
  );
};

const EmptyCell = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: 0.3 }}>
    <Typography sx={{ fontSize: '0.75rem', color: 'grey.700' }}>—</Typography>
  </Box>
);

// Fixed column widths for summary alignment (header + data)
const COL = { invested: 78, pnl: 66, hedge: 66, days: 40, mtna: 72, ttna: 72, net: 52 };

/** Summary column: single row aligned with sort headers */
const SummaryCell = ({ group }: { group: PositionsGroupedByPair }) => {
  const totalPnl = group.totalUnrealizedPnl ?? 0;
  const net = group.netContracts ?? 0;

  return (
    <Stack direction="row" alignItems="center" spacing={0.4} sx={{ py: 0.3 }}>
      <Box sx={{ width: COL.invested, textAlign: 'right' }}>
        <Typography
          sx={{ fontSize: '0.78rem', color: 'grey.400', whiteSpace: 'nowrap' }}
        >
          {formatUsd(group.totalInvested)}
        </Typography>
      </Box>
      <Box sx={{ width: COL.pnl, textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 600,
            color: pnlColor(totalPnl),
            whiteSpace: 'nowrap'
          }}
        >
          {formatUsd(totalPnl)}
        </Typography>
      </Box>
      <Box sx={{ width: COL.hedge, textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 600,
            color: pnlColor(group.hedgePnl ?? undefined),
            whiteSpace: 'nowrap'
          }}
        >
          {group.hedgePnl != null ? formatUsd(group.hedgePnl) : '-'}
        </Typography>
      </Box>
      <Box sx={{ width: COL.days, textAlign: 'center' }}>
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color:
              group.daysToExpiry != null && group.daysToExpiry <= 7
                ? '#ff9800'
                : 'grey.400'
          }}
        >
          {group.daysToExpiry != null ? `${group.daysToExpiry}d` : '-'}
        </Typography>
      </Box>
      <Box sx={{ width: COL.mtna, textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color:
              group.makerTna != null
                ? group.makerTna > 0
                  ? '#4caf50'
                  : '#f44336'
                : 'grey.600'
          }}
        >
          {group.makerTna != null ? `${group.makerTna.toFixed(1)}%` : '-'}
        </Typography>
      </Box>
      <Box sx={{ width: COL.ttna, textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color:
              group.takerTna != null
                ? group.takerTna > 0
                  ? '#4caf50'
                  : '#f44336'
                : 'grey.600'
          }}
        >
          {group.takerTna != null ? `${group.takerTna.toFixed(1)}%` : '-'}
        </Typography>
      </Box>
      <Box sx={{ width: COL.net, textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '0.82rem',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            color: net > 0 ? '#4caf50' : net < 0 ? '#f44336' : 'grey.400'
          }}
        >
          {net > 0 ? '+' : ''}
          {formatContracts(net)}
        </Typography>
      </Box>
    </Stack>
  );
};

const PairRow = ({ group }: { group: PositionsGroupedByPair }) => {
  const polyPos = group.positions.find(p => p.platform === 'polymarket');
  const kalshiPos = group.positions.find(p => p.platform === 'kalshi');
  const isUnmatched = !polyPos || !kalshiPos;

  const polyTitle =
    group.polymarketTitle || polyPos?.pairName || group.pairName || 'Unknown';
  const kalshiTitle =
    group.kalshiTitle || kalshiPos?.pairName || group.pairName || 'Unknown';

  return (
    <Stack
      direction="row"
      alignItems="stretch"
      sx={{
        px: 2,
        py: 0.5,
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        ...(isUnmatched && {
          backgroundColor: 'rgba(255, 152, 0, 0.06)'
        }),
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' }
      }}
    >
      {/* Polymarket column */}
      <Box sx={{ flex: '0 1 33%', minWidth: 0 }}>
        {polyPos ? (
          <PositionCell position={polyPos} title={polyTitle} />
        ) : (
          <EmptyCell />
        )}
      </Box>

      {/* Divider */}
      <Box
        sx={{ width: '1px', mx: 1, backgroundColor: 'rgba(255,255,255,0.08)' }}
      />

      {/* Kalshi column */}
      <Box sx={{ flex: '0 1 33%', minWidth: 0 }}>
        {kalshiPos ? (
          <PositionCell position={kalshiPos} title={kalshiTitle} />
        ) : (
          <EmptyCell />
        )}
      </Box>

      {/* Divider */}
      <Box
        sx={{ width: '1px', mx: 1, backgroundColor: 'rgba(255,255,255,0.08)' }}
      />

      {/* Summary column */}
      <Box sx={{ flexShrink: 0, ml: 'auto', display: 'flex', alignItems: 'center' }}>
        {isUnmatched && (
          <Chip
            label="UNMATCHED"
            size="small"
            sx={{
              mr: 0.8,
              height: 18,
              fontSize: '0.58rem',
              fontWeight: 700,
              color: '#ffb74d',
              borderColor: 'rgba(255, 183, 77, 0.45)'
            }}
            variant="outlined"
          />
        )}
        <SummaryCell group={group} />
      </Box>
    </Stack>
  );
};

const SortButton = ({
  label,
  field,
  activeField,
  activeDir,
  onClick
}: {
  label: string;
  field: SortField;
  activeField: SortField;
  activeDir: SortDir;
  onClick: (field: SortField) => void;
}) => {
  const isActive = activeField === field;
  const Icon =
    isActive && activeDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Chip
      label={
        <Stack direction="row" alignItems="center" spacing={0.3}>
          <span>{label}</span>
          <Icon sx={{ fontSize: '0.75rem', opacity: isActive ? 1 : 0.3 }} />
        </Stack>
      }
      size="small"
      variant={isActive ? 'filled' : 'outlined'}
      onClick={() => onClick(field)}
      sx={{
        fontSize: '0.62rem',
        height: 20,
        cursor: 'pointer',
        borderRadius: '3px',
        ...(isActive && { backgroundColor: 'rgba(255,255,255,0.1)' })
      }}
    />
  );
};

const HeaderRow = ({
  sortField,
  sortDir,
  onSort
}: {
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ px: 2, py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.1)' }}
  >
    {/* Polymarket header */}
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ flex: '0 1 33%', minWidth: 0 }}
    >
      <img
        src="/assets/exchanges/png/100x100/polymarket.png"
        alt="polymarket"
        style={{ width: 16, height: 16, borderRadius: '50%' }}
      />
      <Typography
        sx={{ fontSize: '0.72rem', color: 'grey.500', fontWeight: 600 }}
      >
        Polymarket
      </Typography>
    </Stack>

    <Box sx={{ width: '1px', mx: 1 }} />

    {/* Kalshi header */}
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ flex: '0 1 33%', minWidth: 0 }}
    >
      <img
        src="/assets/exchanges/png/100x100/kalshi.png"
        alt="kalshi"
        style={{ width: 16, height: 16, borderRadius: '50%' }}
      />
      <Typography
        sx={{ fontSize: '0.72rem', color: 'grey.500', fontWeight: 600 }}
      >
        Kalshi
      </Typography>
    </Stack>

    <Box sx={{ width: '1px', mx: 1 }} />

    {/* Summary header with sort buttons — aligned to COL widths */}
    <Stack
      direction="row"
      spacing={0.4}
      alignItems="center"
      sx={{ flexShrink: 0, ml: 'auto' }}
    >
      <Box
        sx={{
          width: COL.invested,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <Typography
          sx={{
            fontSize: '0.6rem',
            color: 'grey.600',
            textTransform: 'uppercase'
          }}
        >
          Invested
        </Typography>
      </Box>
      <Box sx={{ width: COL.pnl, display: 'flex', justifyContent: 'flex-end' }}>
        <SortButton
          label="P&L"
          field="pnl"
          activeField={sortField}
          activeDir={sortDir}
          onClick={onSort}
        />
      </Box>
      <Box
        sx={{
          width: COL.hedge,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <Typography
          sx={{
            fontSize: '0.6rem',
            color: 'grey.600',
            textTransform: 'uppercase'
          }}
        >
          Hedge
        </Typography>
      </Box>
      <Box sx={{ width: COL.days, display: 'flex', justifyContent: 'center' }}>
        <SortButton
          label="Days"
          field="daysToExpiry"
          activeField={sortField}
          activeDir={sortDir}
          onClick={onSort}
        />
      </Box>
      <Box
        sx={{ width: COL.mtna, display: 'flex', justifyContent: 'flex-end' }}
      >
        <SortButton
          label="M.TNA"
          field="makerTna"
          activeField={sortField}
          activeDir={sortDir}
          onClick={onSort}
        />
      </Box>
      <Box
        sx={{ width: COL.ttna, display: 'flex', justifyContent: 'flex-end' }}
      >
        <SortButton
          label="T.TNA"
          field="takerTna"
          activeField={sortField}
          activeDir={sortDir}
          onClick={onSort}
        />
      </Box>
      <Box sx={{ width: COL.net, display: 'flex', justifyContent: 'flex-end' }}>
        <SortButton
          label="Net"
          field="net"
          activeField={sortField}
          activeDir={sortDir}
          onClick={onSort}
        />
      </Box>
    </Stack>
  </Stack>
);

const sortGroupsBy = (
  input: PositionsGroupedByPair[],
  sortField: SortField,
  sortDir: SortDir
) => {
  const sorted = [...input];
  const mult = sortDir === 'desc' ? -1 : 1;
  sorted.sort((a, b) => {
    if (sortField === 'pnl')
      return mult * ((a.totalUnrealizedPnl ?? 0) - (b.totalUnrealizedPnl ?? 0));
    if (sortField === 'makerTna') return mult * ((a.makerTna ?? 0) - (b.makerTna ?? 0));
    if (sortField === 'takerTna') return mult * ((a.takerTna ?? 0) - (b.takerTna ?? 0));
    if (sortField === 'daysToExpiry')
      return mult * ((a.daysToExpiry ?? 9999) - (b.daysToExpiry ?? 9999));
    return mult * (Math.abs(a.netContracts ?? 0) - Math.abs(b.netContracts ?? 0));
  });
  return sorted;
};

const TERMINAL_MARKET_STATUSES = new Set([
  'closed',
  'resolved',
  'settled',
  'finalized',
  'determined',
  'expired'
]);

const isEndedPosition = (position: PredictionPosition) =>
  position.status !== 'open' ||
  position.redeemable === true ||
  position.claimStatus === 'need_claim' ||
  position.claimStatus === 'claimed' ||
  TERMINAL_MARKET_STATUSES.has((position.marketStatus || '').toLowerCase());

const CashflowDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [platform, setPlatform] = useState('kalshi');
  const [type, setType] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const { mutate: createCashflow, isLoading } = useCreateCashflow();

  const handleSubmit = () => {
    if (!amount || isNaN(Number(amount))) return;
    createCashflow(
      {
        platform,
        type,
        amountUsd: Number(amount),
        note: note || undefined,
        date: new Date(date).toISOString()
      },
      {
        onSuccess: () => {
          onClose();
          setAmount('');
          setNote('');
        }
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '0.95rem' }}>Add Deposit / Withdrawal</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <FormControl size="small" fullWidth>
            <InputLabel>Platform</InputLabel>
            <Select
              value={platform}
              label="Platform"
              onChange={e => setPlatform(e.target.value)}
            >
              <MenuItem value="kalshi">Kalshi</MenuItem>
              <MenuItem value="polymarket">Polymarket</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={type} label="Type" onChange={e => setType(e.target.value)}>
              <MenuItem value="deposit">Deposit</MenuItem>
              <MenuItem value="withdrawal">Withdrawal</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Amount (USD)"
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Date"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Note (optional)"
            value={note}
            onChange={e => setNote(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={isLoading || !amount}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PositionsPage = () => {
  const [tab, setTab] = useState(0);
  const [sortField, setSortField] = useState<SortField>('pnl');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [cashflowOpen, setCashflowOpen] = useState(false);
  const { data: openPositionsData } = usePositionsGrouped({ status: 'open' });
  const { data: endedPositionsData } = usePositionsGrouped({ status: 'ended' });
  const { data: stats } = usePositionStats();
  const { data: performance } = usePredictionPerformance();
  const { mutate: triggerSync, isLoading: isSyncing } = useSyncPositions();

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedOpenGroups = useMemo(() => {
    const groups = openPositionsData?.groups ?? [];
    const unmatched = openPositionsData?.unmatched ?? [];
    const merged = [...groups, ...unmatched];
    const visible = merged.filter(group =>
      group.positions.some(p => !isEndedPosition(p))
    );
    return sortGroupsBy(visible, sortField, sortDir);
  }, [openPositionsData, sortField, sortDir]);

  const totalHedgePnl = useMemo(() => {
    return sortedOpenGroups.reduce(
      (sum, g) => sum + (g.hedgePnl ?? 0),
      0
    );
  }, [sortedOpenGroups]);

  const avgMaturityDays = useMemo(() => {
    let totalWeight = 0;
    let weightedDays = 0;
    for (const g of sortedOpenGroups) {
      if (g.daysToExpiry != null && g.totalInvested > 0) {
        totalWeight += g.totalInvested;
        weightedDays += g.daysToExpiry * g.totalInvested;
      }
    }
    return totalWeight > 0 ? Math.round(weightedDays / totalWeight) : null;
  }, [sortedOpenGroups]);

  const sortedEndedGroups = useMemo(() => {
    const groups = endedPositionsData?.groups ?? [];
    const unmatched = endedPositionsData?.unmatched ?? [];
    const all = [...groups, ...unmatched];
    return all.sort((a, b) => {
      const aDate = a.expiryDate ? new Date(a.expiryDate).getTime() : 0;
      const bDate = b.expiryDate ? new Date(b.expiryDate).getTime() : 0;
      return bDate - aDate;
    });
  }, [endedPositionsData]);

  const endedTotalPnl = useMemo(() => {
    return sortedEndedGroups.reduce(
      (sum, g) => sum + (g.totalUnrealizedPnl ?? 0),
      0
    );
  }, [sortedEndedGroups]);

  return (
    <PageWithTitle>
      <Box px={2} py={1} width="100%">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ fontSize: '1.1rem' }}
            >
              Positions
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {stats && (
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="nowrap">
                <Chip label={`Open: ${stats.open}`} size="small" color="primary" variant="outlined" />
                <Chip label={`Invested: ${formatUsd(stats.totalInvested)}`} size="small" variant="outlined" />
                <Chip
                  label={`P&L: ${formatUsd(stats.totalUnrealizedPnl)}`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: pnlColor(stats.totalUnrealizedPnl), color: pnlColor(stats.totalUnrealizedPnl) }}
                />
                <Chip
                  label={`Hedge: ${formatUsd(totalHedgePnl)}`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: pnlColor(totalHedgePnl), color: pnlColor(totalHedgePnl) }}
                />
                {avgMaturityDays != null && (
                  <Chip label={`${avgMaturityDays}d avg`} size="small" variant="outlined" />
                )}
                {performance && (
                  <>
                    <Chip
                      label={`Portfolio: ${formatUsd(performance.pnlUsd)}`}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: pnlColor(performance.pnlUsd), color: pnlColor(performance.pnlUsd) }}
                    />
                    <Tooltip
                      title={
                        <span>
                          Balance: {formatUsd(performance.kalshi.balanceUsd)}
                          <br />
                          Positions: {formatUsd(performance.kalshi.portfolioValueUsd)}
                        </span>
                      }
                      arrow
                    >
                      <Chip
                        label={
                          <Stack direction="row" alignItems="center" spacing={0.3}>
                            <span>Kalshi: {formatUsd(performance.kalshi.totalUsd)}</span>
                            <InfoOutlinedIcon sx={{ fontSize: '0.7rem', opacity: 0.6 }} />
                          </Stack>
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                    <Tooltip
                      title={
                        <span>
                          Positions: {formatUsd(performance.polymarket.positionsValueUsd)}
                          <br />
                          Cash (USDC.e): {formatUsd(performance.polymarket.cashBalanceUsd)}
                        </span>
                      }
                      arrow
                    >
                      <Chip
                        label={
                          <Stack direction="row" alignItems="center" spacing={0.3}>
                            <span>Poly: {formatUsd(performance.polymarket.totalUsd)}</span>
                            <InfoOutlinedIcon sx={{ fontSize: '0.7rem', opacity: 0.6 }} />
                          </Stack>
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                    <Tooltip
                      title={
                        <span>
                          Initial: {formatUsd(performance.initialValue)}
                          <br />
                          Deposits: {formatUsd(performance.cashflows.depositsUsd)}
                          <br />
                          Withdrawals: {formatUsd(performance.cashflows.withdrawalsUsd)}
                        </span>
                      }
                      arrow
                    >
                      <Chip
                        label={
                          <Stack direction="row" alignItems="center" spacing={0.3}>
                            <span>Baseline: {formatUsd(performance.baselineValueUsd)}</span>
                            <InfoOutlinedIcon sx={{ fontSize: '0.7rem', opacity: 0.6 }} />
                          </Stack>
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => setCashflowOpen(true)}
                      sx={{ p: 0.3 }}
                      title="Add deposit / withdrawal"
                    >
                      <AddIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </>
                )}
              </Stack>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={
                isSyncing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SyncIcon />
                )
              }
              onClick={() => triggerSync()}
              disabled={isSyncing}
            >
              Sync
            </Button>
          </Stack>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 1,
            minHeight: 32,
            '& .MuiTab-root': {
              minHeight: 32,
              py: 0,
              textTransform: 'none',
              fontSize: '0.82rem'
            },
            '& .MuiTabs-indicator': { height: 2 }
          }}
        >
          <Tab label={`Open (${(openPositionsData?.groups?.length ?? 0) + (openPositionsData?.unmatched?.length ?? 0)})`} />
          <Tab label={`Ended (${(endedPositionsData?.groups?.length ?? 0) + (endedPositionsData?.unmatched?.length ?? 0)})`} />
        </Tabs>

        {tab === 0 && (
        <Box
          sx={{
            background: 'background.paper',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        >
          <HeaderRow
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
          />
          {sortedOpenGroups.length === 0 ? (
            <Box display="flex" justifyContent="center" py={3}>
              <Typography color="text.secondary" variant="body2">
                No positions
              </Typography>
            </Box>
          ) : (
            sortedOpenGroups.map((group, i) => (
              <PairRow key={group._id ?? `group-${i}`} group={group} />
            ))
          )}
        </Box>
        )}

        {tab === 1 && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Chip
                size="small"
                variant="outlined"
                label={`Total P&L: ${formatUsd(endedTotalPnl)}`}
                sx={{
                  borderColor: pnlColor(endedTotalPnl),
                  color: pnlColor(endedTotalPnl)
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${sortedEndedGroups.length} positions`}
              />
            </Stack>

            <Box
              sx={{
                background: 'background.paper',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              {sortedEndedGroups.length === 0 ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <Typography color="text.secondary" variant="body2">
                    No ended positions
                  </Typography>
                </Box>
              ) : (
                sortedEndedGroups.map((group, i) => (
                  <EndedPairRow key={group._id ?? `ended-${i}`} group={group} />
                ))
              )}
            </Box>
          </Stack>
        )}
        <CashflowDialog open={cashflowOpen} onClose={() => setCashflowOpen(false)} />
      </Box>
    </PageWithTitle>
  );
};
